import { Fragment, useEffect, useState } from "react"
import { useAppDispatch } from "../../../effects/useAppDispatch"
import { FilterGroup, FilterRadioGroup, FilterRangeGroup } from "../../../types/filters/FilterGroup"
import { TFilterType } from "../../../types/filters/FilterState"
import { FilterPeriodCommonType } from "../../../types/state/FilterPeriodState"
import { RadioButton } from "../../controls"
import { createFilterActions } from "../../../actions/filter.actions"
import { numericUtils } from "../../../utils"
import { constants } from "../../../constants"
import { ClickOutside } from "../ClickOutside"
import { FilterButton } from "./FilterButton"
import { FilterDropDown } from "./FilterDropDown"
import { RangeInput } from "./FilterColor"


interface Props {
    defaultExpanded?: boolean
    disabled?: boolean
    group: FilterGroup[]
    filterType: TFilterType
}

export function FilterPeriod({
    group,
    defaultExpanded = false,
    disabled = false,
    filterType,
}: Props) {
    const dispatch = useAppDispatch();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [error, setError] = useState(false);

    const [radio, range] = group as [FilterRadioGroup<FilterPeriodCommonType>, FilterRangeGroup];
    const selectedValue = radio.filter.selectedOption;
    const selectedOption = radio.filter.options.find(o => o.value === selectedValue);
    const isCustom = selectedValue === FilterPeriodCommonType.Custom;

    const isApplied = radio.isApplied;

    const filterActions = createFilterActions(filterType);

    const hasRange =
        numericUtils.isNumber(range.filter.from) ||
        numericUtils.isNumber(range.filter.to);

    const symbolsToCustomText = radio.key.slice(-2);

    useEffect(() => {
        // Swith to All if user doesn't specify a custom range
        if (isCustom && !hasRange && !expanded) {
            dispatch(filterActions.filterRadioClearOption(radio.key));
        }
        // eslint-disable-next-line 
    }, [isCustom, hasRange, expanded, dispatch, radio.key])

    const handleSelectionChange = (value: FilterPeriodCommonType) => {
        if (isCustom) {
            dispatch(filterActions.filterRangeClearSelectedOptions(range.key));
            setError(false);
        }

        dispatch(filterActions.filterRadioChange(value, radio.key));
    }

    function handleClearAll() {
        dispatch(filterActions.filterRadioClearOption(radio.key));
        dispatch(filterActions.filterRangeClearSelectedOptions(range.key));
        setError(false);
    }

    const renderTitle = () => {
        if (isCustom && hasRange && !error) {
            const from = +(range.filter.from ?? constants.filterPeriodEndRange.min);
            const to = +(range.filter.to ?? constants.filterPeriodEndRange.max);

            return `${radio.text}: ${from}Y - ${to}Y`
        }

        return `${radio.text}: ${selectedOption?.text ?? 'All'}`;
    }

    const renderOptions = () => {
        return (
            <ul className="control-filter-select-list">
                {radio.filter.options
                    .filter(o => o.visible)
                    .map(o =>
                        <Fragment key={o.value}>
                            <li>
                                <RadioButton
                                    name={radio.key}
                                    value={o.value}
                                    label={o.text}
                                    checked={o.value === selectedValue || (!selectedValue && o.value === FilterPeriodCommonType.All)}
                                    onChange={e => handleSelectionChange(e.target.value as FilterPeriodCommonType)}
                                />
                            </li>
                            {o.value === FilterPeriodCommonType.No && <li className="list-siparate-label text-warm-grey">remaining period</li>}
                        </Fragment>
                    )}
            </ul>
        );
    }

    return (
        <ClickOutside
            className="control-filter-select control-filter-color"
            onClick={() => setTimeout(() => setExpanded(false))}
        >
            <FilterButton
                title={renderTitle()}
                expanded={expanded}
                applied={isApplied}
                selected={selectedValue != null}
                error={error}
                onExpand={setExpanded}
                onClearAll={handleClearAll}
                disabled={disabled}
            />
            {!disabled && expanded && (
                <FilterDropDown
                    className="control-filter-content-color flex-row"
                    expanded={expanded}
                    value={renderTitle()}
                >
                    <div className="control-filter-content-select">
                        <button
                            className="btn-link"
                            disabled={selectedValue == null}
                            onClick={handleClearAll}
                        >
                            Reset to default
                        </button>
                        {renderOptions()}
                    </div>
                    {isCustom &&
                        <div className="control-filter-content-range">
                            <div className="control-filter-range-text text-sm text-warm-grey">Years until {symbolsToCustomText} period ends. Negative values indicate how long ago {symbolsToCustomText} period ended.</div>
                            <RangeInput
                                range={{
                                    from: numericUtils.isNumber(range.filter.from) ? +range.filter.from : undefined,
                                    to: numericUtils.isNumber(range.filter.to) ? +range.filter.to : undefined
                                }}
                                limits={{ from: constants.filterPeriodEndRange.min, to: constants.filterPeriodEndRange.max }}
                                mask={{
                                    prefix: '',
                                    suffix: '',
                                    includeThousandsSeparator: false,
                                    allowDecimal: true,
                                    decimalLimit: 2,
                                    integerLimit: 3,
                                    requireDecimal: false,
                                    allowNegative: true
                                }}
                                fromPlaceholder={`${constants.filterPeriodEndRange.min}Y`}
                                toPlaceholder={`${constants.filterPeriodEndRange.max}Y`}
                                onChange={(newRange, hasError) => {
                                    setError(hasError);
                                    dispatch(filterActions.changeRangeFilter(newRange, range.key))
                                }}
                            />
                        </div>
                    }
                </FilterDropDown>
            )}
        </ClickOutside>
    )
}